import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Row, Switch } from 'antd';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { memo } from 'react';

import './SwitchableInput.scss';

type SwitchableInputProps = {
  name?: ReactNode;
  subtitle?: string | ReactNode;
  title?: string | ReactNode;
  value: boolean;
  onChange?: (isSwitchOn: boolean) => void;
  childNode: ReactNode;
  disabled?: boolean;
  noBorder?: boolean;
  noBackground?: boolean;
  noPadding?: boolean;
};

export const SwitchableInput = memo((props: SwitchableInputProps) => {
  const {
    name,
    title = name,
    subtitle,
    value,
    onChange,
    childNode,
    disabled,
    noBorder = false,
    noBackground = false,
    noPadding = false,
  } = props;

  return (
    <div
      className={classNames('switchable-input-container', {
        disabled: !value,
        'no-background': noBackground,
        'no-border': noBorder,
        'no-padding': noPadding,
      })}
    >
      {title ? (
        <Header title={title} subtitle={subtitle}>
          <SwitchControl
            checked={value}
            onChange={onChange}
            disabled={disabled}
          />
        </Header>
      ) : (
        <div className="switchable-input-container__switch-only">
          <SwitchControl
            checked={value}
            onChange={onChange}
            disabled={disabled}
          />
        </div>
      )}
      <div className="switchable-input-container__content">{childNode}</div>
    </div>
  );
});

type HeaderProps = {
  title: string | ReactNode;
  subtitle?: string | ReactNode;
  children: ReactNode;
};

const Header = memo(({ title, subtitle, children }: HeaderProps) => (
  <Row justify="space-between" className="switchable-input-container__header">
    <div>
      <div className="switchable-input-container__header__title">{title}</div>
      {subtitle && (
        <div className="switchable-input-container__header__subtitle">
          {subtitle}
        </div>
      )}
    </div>
    {children}
  </Row>
));

type SwitchControlProps = {
  checked: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
};

const SwitchControl = memo(
  ({ checked, onChange, disabled }: SwitchControlProps) => (
    <Switch
      disabled={!!disabled}
      checkedChildren={<CheckOutlined />}
      unCheckedChildren={<CloseOutlined />}
      onChange={onChange}
      checked={checked}
    />
  )
);
